(() => {
  const jewelryWords = 'diamond diamonds ring rings earring earrings necklace necklaces pendant pendants bracelet bracelets bangle bangles chain chains gold silver platinum rose white yellow emerald ruby sapphire pearl pearls oval round cushion radiant princess marquise pear heart solitaire halo vintage engagement wedding anniversary eternity bezel pave stud studs hoop hoops tennis bridal gemstone gemstones jewelry jewellery karat carat cut clarity color'.split(' ');
  const normalize = value => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

  function distance(left, right, maximum) {
    if (Math.abs(left.length - right.length) > maximum) return maximum + 1;
    const rows = [Array.from({ length: right.length + 1 }, (_, index) => index)];
    for (let i = 1; i <= left.length; i++) {
      const row = [i];
      for (let j = 1; j <= right.length; j++) {
        row[j] = Math.min(row[j - 1] + 1, rows[i - 1][j] + 1, rows[i - 1][j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1));
        if (i > 1 && j > 1 && left[i - 1] === right[j - 2] && left[i - 2] === right[j - 1]) row[j] = Math.min(row[j], rows[i - 2][j - 2] + 1);
      }
      rows.push(row);
    }
    return rows[left.length][right.length];
  }

  function vocabulary(values) {
    const phrases = [...new Set((Array.isArray(values) ? values : []).slice(0, 2000).map(normalize).filter(value => /^[a-z ]+$/.test(value) && value.length <= 100))];
    const words = new Set(jewelryWords);
    phrases.forEach(phrase => phrase.split(' ').forEach(word => {
      if (word.length >= 3 && word.length <= 30) words.add(word);
    }));
    return { words, phrases };
  }

  function alternative(query, values, locale = 'en') {
    if (!/^en(?:-|$)/i.test(locale) || !/^[a-zA-Z\s]+$/.test(query) || /\b(?:AND|OR|NOT)\b/.test(query)) return null;
    const original = normalize(query);
    if (original.length < 4 || original.length > 100) return null;
    const terms = original.split(' ');
    if (terms.length > 7) return null;
    const { words, phrases } = vocabulary(values);
    const compact = original.replaceAll(' ', '');
    const spaced = phrases.filter(phrase => phrase !== original && phrase.replaceAll(' ', '') === compact);
    if (spaced.length === 1) return spaced[0];
    const adjusted = [];
    let changed = false;
    for (let index = 0; index < terms.length; index++) {
      const term = terms[index];
      if (index + 1 < terms.length && words.has(term + terms[index + 1]) && !words.has(term)) {
        adjusted.push(term + terms[index + 1]);
        index++;
        changed = true;
        continue;
      }
      if (words.has(term)) {
        const equivalent = { jewelry: 'jewellery', jewellery: 'jewelry' }[term];
        adjusted.push(equivalent || term);
        if (equivalent) changed = true;
        continue;
      }
      const splits = [];
      if (term.length >= 7) {
        for (let split = 3; split <= term.length - 3; split++) {
          if (words.has(term.slice(0, split)) && words.has(term.slice(split))) splits.push(`${term.slice(0, split)} ${term.slice(split)}`);
        }
      }
      if (splits.length === 1) {
        adjusted.push(splits[0]);
        changed = true;
        continue;
      }
      if (terms.length > 1 && index < terms.length - 1 && term.length >= 3) {
        const prefixes = [...words].filter(word => word.startsWith(term)).sort((a, b) => a.length - b.length);
        if (prefixes.length && prefixes.every(word => word.startsWith(prefixes[0]))) {
          adjusted.push(prefixes[0]);
          changed = true;
          continue;
        }
      }
      const maximum = term.length >= 8 ? 2 : term.length >= 4 ? 1 : 0;
      let bestDistance = maximum + 1;
      let matches = [];
      if (maximum) words.forEach(word => {
        const score = distance(term, word, maximum);
        if (score < bestDistance) {
          bestDistance = score;
          matches = [word];
        } else if (score === bestDistance) matches.push(word);
      });
      if (bestDistance <= maximum && matches.length === 1) {
        adjusted.push(matches[0]);
        changed = true;
      } else adjusted.push(term);
    }
    const result = adjusted.join(' ');
    return changed && result !== original ? result : null;
  }

  window.KrownSearchQuery = { alternative, distance };
  if (customElements.get('search-form')) return;
  let instanceNumber = 0;

  class SearchForm extends HTMLElement {
    static cache = new Map();

    constructor() {
      super();
      this._instance = ++instanceNumber;
      this._generation = 0;
      this._selectedIndex = -1;
      this._items = [];
      this._listeners = [];
      this._connected = false;
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      if (this.querySelector('[data-js-search-input]')) this._initialize();
      else this._frame = requestAnimationFrame(() => this._initialize());
    }

    disconnectedCallback() {
      this._connected = false;
      this._invalidate();
      cancelAnimationFrame(this._frame);
      this._listeners.forEach(([node, type, handler]) => node.removeEventListener(type, handler));
      this._listeners = [];
      this._initialized = false;
    }

    _initialize() {
      if (!this._connected || this._initialized) return;
      this.input = this.querySelector('[data-js-search-input]');
      this.results = this.querySelector('[data-js-search-results]');
      this.form = this.querySelector('form');
      if (!this.input || !this.results || !this.form) return;
      this._initialized = true;
      this.status = this.querySelector('[data-js-search-status]');
      this.defaults = this.querySelector('[data-js-search-default]');
      this.correction = this.querySelector('[data-js-search-correction]');
      this._supported = true;
      try {
        const features = document.getElementById('shopify-features');
        if (features && JSON.parse(features.textContent).predictiveSearch === false) this._supported = false;
      } catch {}
      try {
        this._vocabulary = JSON.parse(this.dataset.searchVocabulary || '[]');
      } catch {
        this._vocabulary = [];
      }
      this.results.id ||= `SearchResults-${this._instance}`;
      if (this._supported) {
        this.input.setAttribute('role', 'combobox');
        this.input.setAttribute('aria-autocomplete', 'list');
        this.input.setAttribute('aria-haspopup', 'listbox');
        this.input.setAttribute('aria-controls', this.results.id);
        this.results.setAttribute('role', 'listbox');
      }
      this.input.setAttribute('aria-expanded', 'false');
      this._listen(this.input, 'input', event => {
        if (!this._composing && !event.isComposing) this._handleInput();
      });
      this._listen(this.input, 'compositionstart', () => {
        this._composing = true;
        this._invalidate();
      });
      this._listen(this.input, 'compositionend', () => {
        this._composing = false;
        this._handleInput();
      });
      this._listen(this.input, 'focus', () => {
        if (this.input.value.trim() && (!this.results.children.length || this._renderedInput !== this.input.value.trim())) this._handleInput(0);
        else {
          this._dismissed = false;
          this._open();
        }
      });
      this._listen(this.input, 'keydown', event => this._handleKey(event));
      this._listen(this, 'mousedown', event => {
        const link = event.target.closest('a[href]');
        if (event.button === 0 && link && (this.results.contains(link) || this.correction?.contains(link))) {
          // Keep combobox focus until the link's native click has completed.
          event.preventDefault();
        }
      });
      this._listen(this, 'focusout', event => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        queueMicrotask(() => {
          if (this._connected && !this.contains(document.activeElement)) this._dismiss();
        });
      });
      this.clearButton = this.querySelector('[data-js-search-clear]');
      if (this.clearButton) this._listen(this.clearButton, 'click', () => {
        this.input.value = '';
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.focus();
      });
      this._clear();
      if (document.activeElement === this.input && this.input.value.trim()) this._handleInput(0);
    }

    _listen(node, type, handler) {
      node.addEventListener(type, handler);
      this._listeners.push([node, type, handler]);
    }

    _invalidate() {
      clearTimeout(this._timer);
      this._controller?.abort();
      this._controller = null;
      this._generation++;
    }

    _handleInput(delay = 180) {
      this._invalidate();
      this._dismissed = false;
      this._select(-1);
      this._items = [];
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this._hideCorrection();
      const query = this.input.value.trim();
      if (this.clearButton) this.clearButton.hidden = !this.input.value;
      if (!query || !this._supported) {
        this._clear();
        return;
      }
      if (this.defaults) this.defaults.hidden = true;
      this.dataset.searchState = 'loading';
      this._setBusy(true);
      const generation = this._generation;
      this._timer = setTimeout(() => this._search(query, generation), delay);
    }

    _current(query, generation) {
      return this._connected && generation === this._generation && query === this.input.value.trim();
    }

    _nativeTypes() {
      const configured = String(this.dataset.searchTypes || 'query,product,collection').split(',');
      const allowed = ['product', 'article', 'page'].filter(type => configured.includes(type));
      const requested = String(this.form.querySelector('input[name="type"]')?.value || '').split(',');
      const selected = allowed.filter(type => requested.includes(type));
      return (selected.length ? selected : allowed.length ? allowed : ['product']).join(',');
    }

    _types() {
      const native = this._nativeTypes().split(',');
      const types = ['query'];
      if (native.includes('product')) types.push('product', 'collection');
      if (native.includes('article')) types.push('article');
      if (native.includes('page')) types.push('page');
      return types.join(',');
    }

    _requestURL(query) {
      const settings = window.KROWN?.settings || {};
      const url = new URL(settings.routes?.predictive_search_url || `${window.Shopify?.routes?.root || '/'}search/suggest`, window.location.origin);
      const fields = ['title', 'product_type', 'variants.title', 'vendor', 'body'];
      String(settings.predictive_search_fields || '').split(',').forEach(field => {
        if (['tag', 'variants.sku', 'variants.barcode'].includes(field.trim())) fields.push(field.trim());
      });
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', 'helper-predictive-search');
      url.searchParams.set('resources[type]', this._types());
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][fields]', [...new Set(fields)].join(','));
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url.toString();
    }

    _fullSearchURL(query) {
      const url = new URL(this.form.action || window.KROWN?.settings?.routes?.search_url || '/search', window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('type', this._nativeTypes());
      return url.toString();
    }

    async _fetch(query, signal) {
      const key = this._requestURL(query);
      const cached = SearchForm.cache.get(key);
      if (cached && Date.now() - cached.time < 120000) {
        SearchForm.cache.delete(key);
        SearchForm.cache.set(key, cached);
        return cached.result;
      }
      SearchForm.cache.delete(key);
      const requestController = new AbortController();
      let cancel;
      const cancelled = new Promise((_, reject) => cancel = reject);
      const abort = () => {
        requestController.abort();
        const error = new Error('Search cancelled');
        error.name = 'AbortError';
        cancel(error);
      };
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) abort();
      let deadline;
      let timedOut = false;
      const timeout = new Promise((_, reject) => {
        deadline = setTimeout(() => {
          timedOut = true;
          requestController.abort();
          reject(new Error('Search timeout'));
        }, 8000);
      });
      const request = async () => {
        const response = await fetch(key, { signal: requestController.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Search response ${response.status}`);
        const documentResult = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = documentResult.querySelector('[data-search-response]');
        const count = Number(root?.dataset.resultCount);
        if (!root || !Number.isFinite(count) || count < 0) throw new Error('Missing search results');
        const result = { html: root.outerHTML, count };
        if (!requestController.signal.aborted) {
          SearchForm.cache.set(key, { time: Date.now(), result });
          while (SearchForm.cache.size > 30) SearchForm.cache.delete(SearchForm.cache.keys().next().value);
        }
        return result;
      };
      try {
        return await Promise.race([request(), timeout, cancelled]);
      } catch (error) {
        if (timedOut) throw new Error('Search timeout');
        throw error;
      } finally {
        clearTimeout(deadline);
        signal?.removeEventListener('abort', abort);
      }
    }

    async _search(query, generation) {
      if (!this._current(query, generation)) return;
      this._controller = new AbortController();
      const { signal } = this._controller;
      this._setBusy(true);
      this._announce(this.dataset.searchLoading || '');
      let rendered = false;
      try {
        const native = await this._fetch(query, signal);
        if (!this._current(query, generation)) return;
        this._render(native, query, query);
        rendered = true;
        if (native.count === 0) {
          const suggested = alternative(query, this._vocabulary, this.dataset.searchLocale || document.documentElement.lang || 'en');
          if (suggested) {
            const verified = await this._fetch(suggested, signal);
            if (!this._current(query, generation)) return;
            if (verified.count > 0) {
              this._render(verified, suggested, query);
              this._showCorrection(suggested);
            }
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError' && this._current(query, generation) && !rendered) {
          this.results.replaceChildren();
          this._items = [];
          this._selectedIndex = -1;
          this.dataset.searchState = 'error';
          this._dismiss();
          this._announce(this.dataset.searchError || '');
        }
      } finally {
        if (this._current(query, generation)) this._setBusy(false);
      }
    }

    _render(response, fetchedQuery, inputQuery) {
      this.results.innerHTML = response.html;
      const replacements = new Map();
      this.results.querySelectorAll('[id]').forEach(element => {
        const original = element.id;
        element.id = `${this.results.id}-${original}`;
        replacements.set(original, element.id);
      });
      this.results.querySelectorAll('[for], [aria-labelledby], [aria-describedby], [aria-controls], [aria-owns], [href^="#"]').forEach(element => {
        ['for', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'].forEach(attribute => {
          if (element.hasAttribute(attribute)) element.setAttribute(attribute, element.getAttribute(attribute).split(' ').map(id => replacements.get(id) || id).join(' '));
        });
        const href = element.getAttribute('href');
        if (href?.startsWith('#') && replacements.has(href.slice(1))) element.setAttribute('href', `#${replacements.get(href.slice(1))}`);
      });
      this.results.querySelectorAll('[data-js-search-view-all]').forEach(link => link.href = this._fullSearchURL(fetchedQuery));
      this.results.querySelectorAll('[data-js-search-query]').forEach(link => {
        const query = new URL(link.href, window.location.origin).searchParams.get('q');
        if (query) link.href = this._fullSearchURL(query);
      });
      this._items = [...new Set([...this.results.querySelectorAll('[data-js-search-item]')].map(element => element.matches('a[href]') ? element : element.querySelector('a[href]')).filter(Boolean))];
      this._items.forEach((link, index) => {
        link.id ||= `${this.results.id}-option-${index}`;
        link.setAttribute('role', 'option');
        link.setAttribute('aria-selected', 'false');
        link.setAttribute('tabindex', '-1');
      });
      this._selectedIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this._renderedInput = inputQuery;
      this.dataset.searchState = response.count > 0 ? 'results' : 'empty';
      if (this.defaults) this.defaults.hidden = true;
      const suggestionCount = this._items.filter(link => !link.matches('[data-js-search-view-all]')).length;
      this._announce(this._format(this.dataset.searchCount || '', suggestionCount, 'count'));
      this._open();
    }

    _format(template, value, name) {
      return template.replace(new RegExp(`\\{\\{?\\s*${name}\\s*\\}?\\}`, 'g'), String(value)).replaceAll(`[${name}]`, String(value));
    }

    _showCorrection(query) {
      if (!this.correction) return;
      const message = this.correction.dataset.message || '';
      const pieces = message.split(/\{\{?\s*(?:query|terms)\s*\}?\}|\[(?:query|terms)\]/);
      const link = document.createElement('a');
      link.href = this._fullSearchURL(query);
      link.textContent = query;
      this.correction.replaceChildren(document.createTextNode(pieces[0] || ''), link, document.createTextNode(pieces.length > 1 ? pieces.slice(1).join('') : ''));
      this.correction.hidden = Boolean(this._dismissed);
    }

    _hideCorrection() {
      if (this.correction) {
        this.correction.hidden = true;
        this.correction.replaceChildren();
      }
    }

    _setBusy(busy) {
      this._busy = busy;
      this.results.setAttribute('aria-busy', String(busy));
    }

    _announce(message) {
      if (this.status) this.status.textContent = message;
    }

    _clear() {
      if (!this.results) return;
      this.results.replaceChildren();
      this.results.hidden = true;
      this._items = [];
      this._selectedIndex = -1;
      this._renderedInput = '';
      this.input?.setAttribute('aria-expanded', 'false');
      this.input?.removeAttribute('aria-activedescendant');
      this._setBusy(false);
      this._hideCorrection();
      if (this.clearButton) this.clearButton.hidden = !this.input.value;
      if (this.defaults) this.defaults.hidden = Boolean(this.input.value.trim());
      this.dataset.searchState = 'idle';
      this._announce('');
    }

    _open() {
      if (!this.results?.children.length || this._dismissed || !this.contains(document.activeElement)) return;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      if (this.correction?.children.length) this.correction.hidden = false;
    }

    _dismiss() {
      this._dismissed = true;
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this._select(-1);
      if (this.correction) this.correction.hidden = true;
    }

    _select(index) {
      this._items.forEach((item, itemIndex) => {
        const active = itemIndex === index;
        item.classList.toggle('active', active);
        item.setAttribute('aria-selected', String(active));
      });
      this._selectedIndex = index;
      const item = this._items[index];
      if (!item) {
        this.input?.removeAttribute('aria-activedescendant');
        return;
      }
      this.input.setAttribute('aria-activedescendant', item.id);
      item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    _handleKey(event) {
      if (event.isComposing || this._composing) return;
      if (event.key === 'Escape') {
        if (!this.results.hidden) {
          event.preventDefault();
          event.stopPropagation();
          this._dismiss();
        }
        return;
      }
      if (event.key === 'Enter') {
        const item = this._items[this._selectedIndex];
        if (item && !this._busy && !this.results.hidden) {
          event.preventDefault();
          item.click();
        }
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !this._items.length || this._busy) return;
      event.preventDefault();
      this._dismissed = false;
      this._open();
      const next = event.key === 'ArrowDown' ? Math.min(this._selectedIndex + 1, this._items.length - 1) : Math.max(this._selectedIndex - 1, -1);
      this._select(next);
    }
  }

  customElements.define('search-form', SearchForm);
})();
